import React, { useMemo, useState } from "react";
import './GameBoard.css';

interface Ladder {
  from: number;
  to: number;
}

interface Snake {
  from: number;
  to: number;
}

interface GameBoardProps {
  currentCell: number;
  ladders?: Ladder[];
  snakes?: Snake[];
  special?: {
    startFinish: number;
    checks: number[];
  };
  onCellClick?: (cell: number) => void;
}

interface CellData {
  number: number;
  isStartFinish: boolean;
  isCheck: boolean;
  hasLadder: boolean;
  hasSnake: boolean;
  name?: string;
}

/**
 * Игровое поле 9×8 с нумерацией "змейка"
 * Мобайл-first дизайн с адаптивностью
 */
const GameBoard: React.FC<GameBoardProps> = ({
  currentCell,
  ladders = [],
  snakes = [],
  special = { startFinish: 68, checks: [69, 70, 71] },
  onCellClick
}) => {
  const [tooltipCell, setTooltipCell] = useState<number | null>(null);
  const [tooltipTimeout, setTooltipTimeout] = useState<NodeJS.Timeout | null>(null);

  // Мемоизируем генерацию клеток для производительности
  const cells = useMemo((): CellData[] => {
    const result: CellData[] = [];
    const ladderFroms = new Set(ladders.map(l => l.from));
    const snakeFroms = new Set(snakes.map(s => s.from));
    
    // Генерируем схему "змейка": 8 рядов × 9 столбцов
    for (let row = 7; row >= 0; row--) { // Рендерим сверху вниз (row 7 -> 0)
      const baseStart = row * 9 + 1;
      
      if (row % 2 === 0) {
        // Четные ряды: слева направо (1-9, 17-25, ...)
        for (let col = 0; col < 9; col++) {
          const cellNumber = baseStart + col;
          result.push({
            number: cellNumber,
            isStartFinish: cellNumber === special.startFinish,
            isCheck: special.checks.includes(cellNumber),
            hasLadder: ladderFroms.has(cellNumber),
            hasSnake: snakeFroms.has(cellNumber)
          });
        }
      } else {
        // Нечетные ряды: справа налево (18-10, 26-34, ...)
        for (let col = 8; col >= 0; col--) {
          const cellNumber = baseStart + col;
          result.push({
            number: cellNumber,
            isStartFinish: cellNumber === special.startFinish,
            isCheck: special.checks.includes(cellNumber),
            hasLadder: ladderFroms.has(cellNumber),
            hasSnake: snakeFroms.has(cellNumber)
          });
        }
      }
    }
    
    return result;
  }, [ladders, snakes, special]);

  const handleCellClick = (cellNumber: number, event?: React.MouseEvent) => {
    // Показываем tooltip
    if (tooltipTimeout) {
      clearTimeout(tooltipTimeout);
    }
    
    setTooltipCell(cellNumber);
    
    // Автозакрытие через 1.5 сек
    const timeout = setTimeout(() => {
      setTooltipCell(null);
    }, 1500);
    setTooltipTimeout(timeout);

    // Вызываем callback если есть
    onCellClick?.(cellNumber);
  };

  const getCellLabel = (cell: CellData): string => {
    let label = `Клетка ${cell.number}`;
    if (cell.number === currentCell) label += ', текущая позиция';
    if (cell.isStartFinish) label += ', старт/финиш';
    if (cell.isCheck) label += ', проверка';
    return label;
  };

  const getCellTooltip = (cellNumber: number): string => {
    const cellNames: { [key: number]: string } = {
      1: 'Рождение',
      2: 'Иллюзия',
      3: 'Гнев',
      4: 'Дефицит',
      5: 'Родовая карма',
      6: 'Заблуждение',
      7: 'Тщеславие',
      8: 'Алчность',
      9: 'Желание',
      10: 'Очищение',
      11: 'Новый опыт',
      12: 'Запрет',
      13: 'Ничтожность',
      14: 'Воображение',
      15: 'Фантазия',
      16: 'Ревность',
      17: 'Сострадание',
      18: 'Радость',
      19: 'Карма',
      20: 'Благотворительность',
      21: 'Прощение',
      22: 'Дхарма',
      23: 'Сбалансированная личность',
      24: 'Плохая компания',
      25: 'Печаль',
      26: 'Удовлетворённость',
      27: 'Служение',
      28: 'Предназначение',
      29: 'Слепая вера',
      30: 'Хорошие тенденции',
      31: 'Святость',
      32: 'Равновесие',
      33: 'Аромат',
      34: 'Красота мира',
      35: 'Чистилище',
      36: 'Образ мира',
      37: 'Инсайт',
      38: 'Вдохновение',
      39: 'Переплетение связей',
      40: 'Денежный канал',
      41: 'Самореализация',
      42: 'Медные трубы',
      43: 'Человек-Творец',
      44: 'Слабость',
      45: 'Сила',
      46: 'Различение',
      47: 'Наблюдатель',
      48: 'Солнечный канал',
      49: 'Лунный канал',
      50: 'Третий глаз',
      51: 'Земля',
      52: 'Внутренний родитель',
      53: 'Вода',
      54: 'Безусловность',
      55: 'Внутренний ребёнок',
      56: 'Воздух',
      57: 'Огонь',
      58: 'Пространство',
      59: 'Внутренний взрослый',
      60: 'Позитивный интеллект',
      61: 'Страх',
      62: 'Счастье',
      63: 'Сансара',
      64: 'Раджас',
      65: 'Саттва',
      66: 'Блаженство',
      67: 'Космическое благо',
      68: 'Космическое сознание',
      69: 'Новая реальность',
      70: 'Новая цель',
      71: 'Новая деятельность',
      72: 'Тамагуна'
    };
    
    return cellNames[cellNumber] || `Клетка ${cellNumber}`;
  };

  // Функция для определения позиции tooltip
  const getTooltipPosition = (cellNumber: number): string => {
    // Простая логика позиций tooltip
    const positionMap: { [key: number]: string } = {
      // Столбцы по левой границе + следующий столбец: tooltip сверху влево
      72: 'top-left', 55: 'top-left', 54: 'top-left', 37: 'top-left', 36: 'top-left', 19: 'top-left', 18: 'top-left', 1: 'top-left',
      71: 'top-left', 56: 'top-left', 53: 'top-left', 38: 'top-left', 35: 'top-left', 20: 'top-left', 17: 'top-left', 2: 'top-left',
      
      // Столбцы по правой границе + предыдущий столбец: tooltip сверху вправо  
      64: 'top-right', 63: 'top-right', 46: 'top-right', 45: 'top-right', 28: 'top-right', 27: 'top-right', 10: 'top-right', 9: 'top-right',
      65: 'top-right', 62: 'top-right', 47: 'top-right', 44: 'top-right', 29: 'top-right', 26: 'top-right', 11: 'top-right', 8: 'top-right',
      
      // Все остальные столбцы (3-7): tooltip по центру
      70: 'top', 69: 'top', 68: 'top', 67: 'top', 66: 'top',
      61: 'top', 60: 'top', 59: 'top', 58: 'top', 57: 'top',
      52: 'top', 51: 'top', 50: 'top', 49: 'top', 48: 'top',
      43: 'top', 42: 'top', 41: 'top', 40: 'top', 39: 'top',
      34: 'top', 33: 'top', 32: 'top', 31: 'top', 30: 'top',
      25: 'top', 24: 'top', 23: 'top', 22: 'top', 21: 'top',
      16: 'top', 15: 'top', 14: 'top', 13: 'top', 12: 'top',
      7: 'top', 6: 'top', 5: 'top', 4: 'top', 3: 'top'
    };
    
    return positionMap[cellNumber] || 'center';
  };

  return (
    <div className="gameboard-wrapper">
      <div className="gameboard-container">
        {cells.map((cell) => {
          const isCurrent = cell.number === currentCell;
          const isAdjacent = currentCell && Math.abs(cell.number - currentCell) <= 1;
          
          let cellClass = "gameboard-cell";
          if (isCurrent) cellClass += " current";
          if (isAdjacent) cellClass += " adjacent";
          if (cell.isStartFinish) cellClass += " start-finish";
          if (cell.isCheck) cellClass += " check";
          
          return (
            <button
              key={cell.number}
              className={cellClass}
              onClick={(e) => handleCellClick(cell.number, e)}
              aria-label={getCellLabel(cell)}
              type="button"
            >
              <span className="cell-number">{cell.number}</span>
              
              {/* Метки для стрел и змей */}
              <div className="cell-markers">
                {cell.hasLadder && (
                  <span className="marker ladder" aria-hidden="true">🪜</span>
                )}
                {cell.hasSnake && (
                  <span className="marker snake" aria-hidden="true">🐍</span>
                )}
              </div>
              
              {/* Бейдж для старт/финиш */}
              {cell.isStartFinish && (
                <span className="badge start-finish-badge">Старт/Финиш</span>
              )}
              
              {/* Звездочка для проверок */}
              {cell.isCheck && (
                <span className="check-star" aria-hidden="true">⭐</span>
              )}
              
              {/* Tooltip с умным позиционированием */}
              {tooltipCell === cell.number && (
                <div 
                  className={`cell-tooltip ${getTooltipPosition(cell.number)}`} 
                  role="tooltip"
                >
                  {getCellTooltip(cell.number)}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default GameBoard;
